import React, { useEffect, useState } from 'react';
import { XIcon, SearchIcon, ClockIcon, XCircleIcon } from 'lucide-react';
import { SearchBar } from './SearchBar';
interface SearchResult {
  id: string;
  name: string;
  type: 'artist' | 'album' | 'playlist' | 'track';
  imageUrl: string;
  subtext?: string;
}
interface RecentSearch {
  id: string;
  title: string;
  subtitle: string;
  imageUrl?: string;
  type: string;
}
interface SearchPopupProps {
  onClose: () => void;
}
const RECENT_SEARCHES: RecentSearch[] = [{
  id: '1',
  title: 'Taylor Swift',
  subtitle: 'Artist',
  imageUrl: 'https://i.scdn.co/image/ab6761610000e5eb5a00969a4698c3132a15fbb0',
  type: 'artist'
}, {
  id: '2',
  title: 'Podcast Daily',
  subtitle: 'Podcast • Spotify',
  imageUrl: 'https://i.scdn.co/image/ab6765630000ba8a141bbfb6056adda2d057c722',
  type: 'podcast'
}, {
  id: '3',
  title: 'Lofi Beats',
  subtitle: 'Playlist • Spotify',
  imageUrl: 'https://i.scdn.co/image/ab67706f00000002ca5a7517156021292e5663a6',
  type: 'playlist'
}];
const MOCK_SEARCH_RESULTS: SearchResult[] = [{
  id: '1',
  name: 'The Weeknd',
  type: 'artist',
  imageUrl: 'https://i.scdn.co/image/ab6761610000e5eb8ae7f2aaa9817a704a87ea36'
}, {
  id: '2',
  name: 'After Hours',
  type: 'album',
  imageUrl: 'https://i.scdn.co/image/ab67616d00001e02ef10c30dfca2f5c3a4426b03',
  subtext: 'The Weeknd'
}, {
  id: '3',
  name: 'Blinding Lights',
  type: 'track',
  imageUrl: 'https://i.scdn.co/image/ab67616d00001e02ef10c30dfca2f5c3a4426b03',
  subtext: 'The Weeknd'
}, {
  id: '4',
  name: 'Weeknd Essentials',
  type: 'playlist',
  imageUrl: 'https://i.scdn.co/image/ab67706f00000002c414e7daf34690c9f983f76e',
  subtext: 'Spotify'
}];
export const SearchPopup = ({
  onClose
}: SearchPopupProps) => {
  const [query, setQuery] = useState('');
  const [results, setResults] = useState<SearchResult[]>([]);
  const [recentSearches, setRecentSearches] = useState<RecentSearch[]>(RECENT_SEARCHES);
  const [isMobile, setIsMobile] = useState(false);
  useEffect(() => {
    const checkIfMobile = () => {
      setIsMobile(window.innerWidth < 768);
    };
    // Initial check
    checkIfMobile();
    // Add event listener for window resize
    window.addEventListener('resize', checkIfMobile);
    // Cleanup
    return () => window.removeEventListener('resize', checkIfMobile);
  }, []);
  useEffect(() => {
    // Auto-focus the search input when the component mounts
    const searchInput = document.getElementById('search-input');
    if (searchInput) {
      searchInput.focus();
    }
  }, []);
  const handleSearch = (query: string) => {
    setQuery(query);
    if (query.trim().length > 0) {
      // Simulate search results - in a real app, you'd call an API
      setResults(MOCK_SEARCH_RESULTS.filter(result => result.name.toLowerCase().includes(query.toLowerCase())));
    } else {
      setResults([]);
    }
  };
  const clearSearch = () => {
    setQuery('');
    setResults([]);
  };
  const removeRecentSearch = (id: string) => {
    setRecentSearches(recentSearches.filter(item => item.id !== id));
  };
  const clearAllRecentSearches = () => {
    setRecentSearches([]);
  };
  const containerClassName = isMobile ? 'fixed inset-0 z-50 bg-black flex flex-col' : 'fixed inset-0 z-50 bg-black/70 flex items-start justify-center pt-16';
  const contentClassName = isMobile ? 'bg-[#282828] flex flex-col flex-1 w-full' : 'bg-[#282828] rounded-lg w-full max-w-3xl mx-4 overflow-hidden';
  return <div className={containerClassName} data-id="element-561">
      <div className={contentClassName} data-id="element-562">
        <div className="p-4 flex items-center border-b border-white/10" data-id="element-563">
          <button onClick={onClose} className="p-2 mr-2 hover:bg-white/10 rounded-full transition-colors" data-id="element-564">
            <XIcon className="w-6 h-6" data-id="element-565" />
          </button>
          <div className="relative flex-1" data-id="element-566">
            <SearchBar placeholder="What do you want to listen to?" onSearch={handleSearch} recentSearches={[]} onClearRecentSearches={() => {}} onRemoveRecentSearch={() => {}} data-id="element-567" />
          </div>
        </div>
        <div className={`${isMobile ? 'flex-1 overflow-y-auto' : 'max-h-[70vh] overflow-y-auto'} p-4`} data-id="element-568">
          {query ?
        // Search results
        <div data-id="element-569">
              <h2 className="text-xl font-bold mb-4" data-id="element-570">Top results</h2>
              {results.length > 0 ? <div className="grid grid-cols-1 md:grid-cols-2 gap-2" data-id="element-571">
                  {results.map(result => <div key={result.id} className="flex items-center p-2 rounded-md hover:bg-white/10 cursor-pointer" data-id="element-572">
                      <img src={result.imageUrl || 'https://images.unsplash.com/photo-1611339555312-e607c8352fd7?w=300&h=300&fit=crop'} alt={result.name} className={`mr-3 ${result.type === 'artist' ? 'rounded-full' : 'rounded'}`} style={{
                width: '48px',
                height: '48px'
              }} onError={e => {
                e.currentTarget.src = 'https://images.unsplash.com/photo-1611339555312-e607c8352fd7?w=300&h=300&fit=crop';
              }} data-id="element-573" />
                      <div className="overflow-hidden flex-1" data-id="element-574">
                        <div className="font-medium truncate" data-id="element-575">
                          {result.name}
                        </div>
                        <div className="flex items-center text-sm text-gray-400 truncate" data-id="element-576">
                          <span className="capitalize" data-id="element-577">{result.type}</span>
                          {result.subtext && <>
                              <span className="mx-1" data-id="element-578">•</span>
                              <span className="truncate" data-id="element-579">{result.subtext}</span>
                            </>}
                        </div>
                      </div>
                    </div>)}
                </div> : <p className="text-gray-400" data-id="element-580">No results found for "{query}"</p>}
            </div> :
        // Recent searches
        <div data-id="element-581">
              <div className="flex justify-between items-center mb-4" data-id="element-582">
                <h2 className="text-xl font-bold" data-id="element-583">Recent searches</h2>
                {recentSearches.length > 0 && <button onClick={clearAllRecentSearches} className="text-sm text-gray-400 hover:text-white" data-id="element-584">
                    Clear all
                  </button>}
              </div>
              {recentSearches.length > 0 ? <div className="space-y-2" data-id="element-585">
                  {recentSearches.map(item => <div key={item.id} className="flex items-center justify-between p-2 rounded-md hover:bg-white/10 cursor-pointer group" data-id="element-586">
                      <div className="flex items-center overflow-hidden" data-id="element-587">
                        {item.imageUrl ? <img src={item.imageUrl || 'https://images.unsplash.com/photo-1611339555312-e607c8352fd7?w=300&h=300&fit=crop'} alt={item.title} className={`mr-3 ${item.type === 'artist' ? 'rounded-full' : 'rounded'}`} style={{
                  width: '40px',
                  height: '40px'
                }} onError={e => {
                  e.currentTarget.src = 'https://images.unsplash.com/photo-1611339555312-e607c8352fd7?w=300&h=300&fit=crop';
                }} data-id="element-588" /> : <div className="mr-3 bg-[#333] rounded-full w-10 h-10 flex items-center justify-center" data-id="element-589">
                            <ClockIcon className="w-5 h-5 text-white" data-id="element-590" />
                          </div>}
                        <div className="overflow-hidden" data-id="element-591">
                          <div className="font-medium truncate" data-id="element-592">
                            {item.title}
                          </div>
                          <div className="text-sm text-gray-400 truncate" data-id="element-593">
                            {item.subtitle}
                          </div>
                        </div>
                      </div>
                      <button onClick={e => {
                e.stopPropagation();
                removeRecentSearch(item.id);
              }} className="opacity-0 group-hover:opacity-100 p-2 hover:bg-white/10 rounded-full" data-id="element-594">
                        <XIcon className="w-4 h-4 text-gray-400 hover:text-white" data-id="element-595" />
                      </button>
                    </div>)}
                </div> : <p className="text-gray-400" data-id="element-596">No recent searches</p>}
            </div>}
        </div>
        {isMobile && <div className="p-4 border-t border-white/10" data-id="element-597">
            <button onClick={onClose} className="w-full bg-white text-black font-bold py-3 rounded-full" data-id="element-598">
              Close
            </button>
          </div>}
      </div>
    </div>;
};